/**
 * Operatori di ricerca per il sistema di filtri
 *
 * @description
 * Definisce tutti gli operatori disponibili per costruire query di ricerca.
 * Ogni operatore è rappresentato da un Symbol univoco per garantire type safety
 * e prevenire errori di comparazione accidentali.
 */

/**
 * Enum degli operatori di ricerca disponibili
 *
 * @example
 * // Utilizzo degli operatori di ricerca
 * import { SearchOperator } from '@/mixins/filterHandler/lib';
 *
 * const filter = new StringFilter('name')
 *   .contains(); // Utilizza SearchOperator.CONTAINS internamente
 *
 * // Verifica dell'operatore
 * if (filter._searchOperator === SearchOperator.CONTAINS) {
 *   console.log('Il filtro usa l\'operatore contains');
 * }
 */
export const SearchOperator = Object.freeze({
	/** Nessuna operazione - filtro disabilitato */
	NOOP: Symbol('-'),
	/** Uguaglianza esatta (=) */
	EQUAL: Symbol('eq'),
	/** Contiene la stringa specificata */
	CONTAINS: Symbol('cnt'),
	/** Inizia con la stringa specificata */
	STARTSWITH: Symbol('stw'),
	/** Termina con la stringa specificata */
	ENDSWITH: Symbol('enw'),
	/** Diverso da (≠) */
	NOTEQUAL: Symbol('ne'),
	/** Maggiore di (>) */
	GREATERTHAN: Symbol('gt'),
	/** Maggiore o uguale a (≥) */
	GREATEREQUAL: Symbol('ge'),
	/** Minore di (<) */
	LESSERTHAN: Symbol('lt'),
	/** Minore o uguale a (≤) */
	LESSEREQUAL: Symbol('le'),
	/** Il valore è contenuto nell'array specificato */
	IN: Symbol('in'),
	/** Il valore NON è compreso tra i due valori specificati */
	NOTBETWEEN: Symbol('nbtw'),
	/** Il valore è compreso tra i due valori specificati */
	BETWEEN: Symbol('btw'),
	/** Almeno un elemento della collezione soddisfa la condizione */
	ANY: Symbol('any'),
	/** Tutti gli elementi della collezione soddisfano la condizione */
	ALL: Symbol('all'),
	/** Il valore è null */
	ISNULL: Symbol('isnull'),
	/** Il valore non è null */
	ISNOTNULL: Symbol('isnotnull'),
} as const);

/**
 * Tipo derivato per gli operatori di ricerca
 */
export type SearchOperatorType = (typeof SearchOperator)[keyof typeof SearchOperator];

/**
 * Operatori di ordinamento per il sistema di filtri
 *
 * @description
 * Definisce gli operatori disponibili per ordinare i risultati delle query.
 *
 * @example
 * // Utilizzo degli operatori di ordinamento
 * import { OrderOperator, Order } from '@/mixins/filterHandler/lib';
 *
 * const order = new Order()
 *   .by('name', OrderOperator.ASCENDING)
 *   .thenBy('date', OrderOperator.DESCENDING);
 */
export const OrderOperator = Object.freeze({
	/** Ignora l'ordinamento per questa proprietà */
	IGNORE: Symbol('-'),
	/** Ordinamento crescente (A-Z, 0-9) */
	ASCENDING: Symbol('asc'),
	/** Ordinamento decrescente (Z-A, 9-0) */
	DESCENDING: Symbol('desc'),
} as const);

/**
 * Tipo derivato per gli operatori di ordinamento
 */
export type OrderOperatorType = (typeof OrderOperator)[keyof typeof OrderOperator];
